import { supabase } from '../lib/supabase';
import { assertContentIsAllowed } from '../lib/contentModeration';

import { User } from '../types';

export interface Story {
  id: string;
  user_id: string;
  content_type: 'image' | 'video' | 'text';
  content_url?: string;
  text_content?: string;
  background_color: string;
  duration: number;
  created_at: string;
  expires_at: string;
  user?: User;
  views_count?: number;
  is_viewed?: boolean;
}

export interface StoryView {
  id: string;
  story_id: string;
  viewer_id: string;
  viewed_at: string;
  profiles: User;
}

export interface StoryGroup {
  user: User;
  stories: Story[];
  latest_story: string;
  is_viewed: boolean;
}

export const storiesService = {
  async uploadStoryFile(file: File, userId: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/story-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('stories')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('stories')
      .getPublicUrl(fileName);

    return data.publicUrl;
  },

  async getActiveStories() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Get current user's following list
    const { data: followingData } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);

    const followingIds = followingData?.map(f => f.following_id) || [];

    // Get premium users
    const { data: premiumUsers } = await supabase
      .from('profiles')
      .select('id')
      .eq('is_premium', true);

    const premiumUserIds = premiumUsers?.map(p => p.id) || [];

    // Combine all user IDs that should be visible
    const visibleUserIds = [
      user.id,           // Current user's stories
      ...followingIds,   // Following users' stories
      ...premiumUserIds  // Premium users' stories
    ];

    // Remove duplicates
    const uniqueUserIds = [...new Set(visibleUserIds)];

    const { data, error } = await supabase
      .from('stories')
      .select(`
        *,
        profiles!stories_user_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type
        ),
        story_views (
          id,
          viewer_id
        )
      `)
      .gt('expires_at', new Date().toISOString())
      .in('user_id', uniqueUserIds)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Group stories by user
    const groupedStories: { [key: string]: StoryGroup } = {};

    data?.forEach((story) => {
      const userId = story.user_id;
      const isViewed = user ? story.story_views?.some((view: { viewer_id: string }) => view.viewer_id === user.id) : false;

      if (!groupedStories[userId]) {
        groupedStories[userId] = {
          user: story.profiles,
          stories: [],
          latest_story: story.created_at,
          is_viewed: false,
        };
      }

      groupedStories[userId].stories.push({
        ...story,
        user: story.profiles,
        views_count: story.story_views?.length || 0,
        is_viewed: isViewed,
      });

      // Update if this story is newer
      if (new Date(story.created_at) > new Date(groupedStories[userId].latest_story)) {
        groupedStories[userId].latest_story = story.created_at;
      }

      // If any story is viewed, mark group as viewed
      if (isViewed) {
        groupedStories[userId].is_viewed = true;
      }
    });

    // Convert to array and sort by latest story
    return Object.values(groupedStories).sort((a, b) =>
      new Date(b.latest_story).getTime() - new Date(a.latest_story).getTime()
    );
  },

  async getMyStories() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('stories')
      .select(`
        *,
        story_views (
          id,
          viewer_id,
          viewed_at,
          profiles!story_views_viewer_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('user_id', user.id)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(story => ({
      ...story,
      views_count: story.story_views?.length || 0,
    })) || [];
  },

  async createStory(storyData: {
    content_type: 'image' | 'video' | 'text';
    content_url?: string;
    text_content?: string;
    background_color?: string;
    duration?: number;
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    if (storyData.content_type === 'text') {
      await assertContentIsAllowed([storyData.text_content]);
    }

    // Calculate expiration time (24 hours from now)
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    const { data, error } = await supabase
      .from('stories')
      .insert({
        user_id: user.id,
        content_type: storyData.content_type,
        content_url: storyData.content_url || null,
        text_content: storyData.text_content || null,
        background_color: storyData.background_color || '#9333ea',
        duration: storyData.duration || 5,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async viewStory(storyId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check if already viewed
    const { data: existing } = await supabase
      .from('story_views')
      .select('id')
      .eq('story_id', storyId)
      .eq('viewer_id', user.id)
      .single();

    if (existing) return; // Already viewed

    const { error } = await supabase
      .from('story_views')
      .insert({
        story_id: storyId,
        viewer_id: user.id,
      });

    if (error) console.error('Error marking story as viewed:', error);
  },

  async deleteStory(storyId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('stories')
      .delete()
      .eq('id', storyId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async getStoryViews(storyId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('story_views')
      .select(`
        *,
        profiles!story_views_viewer_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        )
      `)
      .eq('story_id', storyId)
      .in('story_id', [
        // Only allow viewing views for own stories
        await supabase
          .from('stories')
          .select('id')
          .eq('user_id', user.id)
          .then(({ data }) => data?.map(s => s.id) || [])
      ])
      .order('viewed_at', { ascending: false });

    if (error) throw error;
    return (data || []) as unknown as StoryView[];
  },

  // Clean up expired stories (should be called periodically)
  async cleanupExpiredStories() {
    const { error } = await supabase.rpc('cleanup_expired_stories');
    if (error) console.error('Error cleaning up expired stories:', error);
  },
};
